import React from 'react';
// import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
} from '@mui/material';

import testSetup from '../assets/qbtest/QbTest Telehealth_official_002.jpg';
import qbTestLogo from '../assets/qbtest/Qbtech_Colour.png';
import adhdBackground from '../assets/qbtest/QbTest Telehealth_official_001.webp';
const zocdocLink =
  'https://www.zocdoc.com/practice/aspire-bhp-179924?lock=true&isNewPatient=false&referrerType=widget';
 
const highlights = [
  'FDA-cleared ADHD assessment tool',
  'Measures attention, activity, and impulsivity',
  'Available for ages 6–60',
  'Can support diagnosis and treatment monitoring',
  'Provides visual reports for clearer results',
  'Available in-office or through telehealth options',
];

const pathway = [
  {
    title: 'ADHD Evaluation',
    description:
      'Your provider reviews your symptoms, personal history, and concerns before completing the QbTest® assessment.',
  },
  {
    title: 'Results Review',
    description:
      'Your results are reviewed with your clinician using a visual report that helps explain attention, activity, and impulsivity patterns.',
  },
  {
    title: 'Treatment Planning',
    description:
      'Your provider works with you to create a personalized care plan that may include medication management, therapy, coaching, or follow-up support.',
  },
];

const details = [
  {
    title: 'What is QbTest®?',
    description:
      'QbTest® is an FDA-cleared, objective ADHD assessment tool that combines a computer-based task with motion tracking. It helps measure core ADHD symptoms including inattention, hyperactivity, and impulsivity.',
    image: qbTestLogo,
  },
  {
    title: 'What Does the Test Setup Look Like?',
    description:
      'The test typically takes about 15–20 minutes and provides detailed visual results. Depending on your care plan, testing may be completed in-office or through a telehealth option using a computer and webcam.',
    image: testSetup,
  },
];

export default function QbTest() {
  return (
    <>
      <Helmet>
        <title>QbTest® ADHD Assessment | Aspire Behavioral Health</title>
        <meta
          name="description"
          content="Aspire Behavioral Health offers QbTest® ADHD assessments to objectively measure attention, activity, and impulsivity for clearer diagnosis and treatment planning."
        />
      </Helmet>

      {/* Hero Section */}
    <Box
      sx={{
        position: 'relative',

        minHeight: {
          xs: 520,
          sm: 580,
          md: 650,
          lg: 620,
        },

        py: {
          xs: 8,
          sm: 10,
          md: 12,
          lg: 10,
        },

        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        color: 'white',
      }}
    >
        <Box
          component="img"
          src={adhdBackground}
          alt="QbTest ADHD assessment at Aspire Behavioral Health"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            zIndex: -2,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            zIndex: -1,
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            px: 3,
            maxWidth: '850px',
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 600,
              mb: 2,
              fontSize: { xs: '2.2rem', sm: '3.5rem' },
              lineHeight: 1.2,
            }}
          >
            QbTest® ADHD Assessment
          </Typography>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 300,
              lineHeight: 1.8,
              mb: 4,
              fontSize: { xs: '1rem', sm: '1.2rem' },
            }}
          >
            Objective ADHD testing designed to bring clarity, confidence, and
            personalized support to your care plan.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              href="tel:2108669850"
              sx={{
                backgroundColor: '#e3f3c5',
                color: 'black',
                fontWeight: 600,
                px: 4,
                py: 1.3,
                borderRadius: '30px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#d3eab0',
                },
              }}
            >
              Call 210-866-9850
            </Button>

            <Button
                          component="a"
                          href={zocdocLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                          sx={{
                            borderColor: 'white',
                            color: 'white !important',
                            fontWeight: 600,
                            px: 4,
                            py: 1.3,
                            borderRadius: '30px',
                            textTransform: 'none',
                            '&:visited': {
                              color: 'white',
                            },
                            '&:hover': {
                              borderColor: 'white',
                              backgroundColor: 'rgba(255,255,255,0.1)',
                              color: 'white',
                            },
                          }}
                        >
                          Schedule an Appointment
                        </Button>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ backgroundColor: 'white', py: 8, px: 2 }}>
        {/* Intro Section */}
        <Box sx={{ textAlign: 'center', maxWidth: '900px', mx: 'auto', mb: 7 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
            Clearer Answers for ADHD Concerns
          </Typography>

          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.9,
              color: 'text.secondary',
              fontSize: { xs: '1rem', sm: '1.05rem' },
            }}
          >
            At Aspire Behavioral Health, we take a comprehensive,
            patient-centered approach to ADHD evaluation. Our team reviews your
            symptoms, personal history, and goals to better understand what you
            are experiencing.
            <br />
            <br />
            QbTest® combines computer-based tasks with motion tracking to
            objectively measure attention, activity, and impulsivity. These
            results help your provider make more informed treatment decisions and
            help you better understand your symptoms.
            <br />
            <br />
            To schedule your evaluation, call{' '}
            <a href="tel:2108669850">210-866-9850</a>.
          </Typography>
        </Box>

        <Divider
          sx={{
            width: { xs: '80%', sm: '60%', md: '40%' },
            mx: 'auto',
            mb: 7,
            borderColor: '#d9d9d9',
          }}
        />

        {/* Highlights */}
        <Box
          sx={{
            backgroundColor: '#e3f3c5',
            color: 'black',
            maxWidth: '1200px',
            mx: 'auto',
            p: { xs: 3, sm: 5 },
            borderRadius: 4,
            boxShadow: '0px 4px 12px rgba(0,0,0,0.12)',
            mb: 8,
          }}
        >
          <Typography
            variant="h4"
            sx={{ textAlign: 'center', fontWeight: 600, mb: 2 }}
          >
            What QbTest® Helps Measure
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              maxWidth: '750px',
              mx: 'auto',
              mb: 4,
              lineHeight: 1.7,
            }}
          >
            QbTest® gives your provider objective information that can support a
            more complete ADHD evaluation.
          </Typography>

          <Grid container spacing={2} justifyContent="center" alignItems="stretch">
            {highlights.map((item) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={item}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Card
                  sx={{
                    width: '100%',
                    maxWidth: 340,
                    borderRadius: 3,
                    boxShadow: 'none',
                    backgroundColor: 'rgba(255,255,255,0.75)',
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {item}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Detail Cards */}
        <Box sx={{ maxWidth: '1000px', mx: 'auto', mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              mb: 2,
            }}
          >
            About the Assessment
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              maxWidth: '750px',
              mx: 'auto',
              mb: 5,
              lineHeight: 1.8,
              color: 'text.secondary',
            }}
          >
            The goal is to give you and your provider a clearer picture of your
            symptoms so your care plan can be more personalized.
          </Typography>

          {details.map((item) => (
            <Card
              key={item.title}
              sx={{
                borderRadius: 4,
                boxShadow: '0px 4px 14px rgba(0,0,0,0.1)',
                mb: 4,
              }}
            >
              <CardContent
                sx={{
                  p: { xs: 3, sm: 5 },
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {item.image && (
                  <Box
                    component="img"
                    src={item.image}
                    alt={item.title}
                    sx={{
                      width: '100%',
                      maxWidth: 240,
                      height: 'auto',
                      borderRadius: 3,
                      objectFit: 'contain',
                    }}
                  />
                )}

                <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                    {item.title}
                  </Typography>

                  <Typography sx={{ lineHeight: 1.9, color: 'text.secondary' }}>
                    {item.description}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Care Pathway */}
        <Box
          sx={{
            maxWidth: '1100px',
            mx: 'auto',
            mb: 8,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              mb: 2,
            }}
          >
            The Care Pathway
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              maxWidth: '750px',
              mx: 'auto',
              mb: 5,
              lineHeight: 1.8,
              color: 'text.secondary',
            }}
          >
            From evaluation to treatment planning, our team guides you through
            each step.
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {pathway.map((step, index) => (
              <Grid item xs={12} sm={6} md={4} key={step.title}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    boxShadow: '0px 4px 14px rgba(0,0,0,0.1)',
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        color: '#3a4c17',
                      }}
                    >
                      Step {index + 1}
                    </Typography>

                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      {step.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ lineHeight: 1.8, color: 'text.secondary' }}
                    >
                      {step.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
            Ready to schedule an ADHD evaluation?
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              href="tel:2108669850"
              sx={{
                backgroundColor: '#e3f3c5',
                color: 'black',
                fontWeight: 600,
                px: 4,
                py: 1.3,
                borderRadius: '30px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#d3eab0',
                },
              }}
            >
              Call 210-866-9850
            </Button>

            <Button
                            component="a"
                            href={zocdocLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            sx={{
                              backgroundColor: 'transparent',
                              border: '2px solid #3a4c17',
                              color: '#3a4c17 !important',
                              fontWeight: 600,
                              px: 4,
                              py: 1.3,
                              borderRadius: '30px',
                              textTransform: 'none',
                              '&:visited': {
                                color: '#3a4c17',
                              },
                              '&:hover': {
                                backgroundColor: 'rgba(58,76,23,0.05)',
                                border: '2px solid #3a4c17',
                                color: '#3a4c17',
                              },
                            }}
                          >
                            Schedule an Appointment
                          </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
}